import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings, Radio, Tags, DollarSign, Plus, FileDown } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DeleteBtn, Loading, handleErr } from "./AdminUI";
import { ImageUpload } from "@/components/ImageUpload";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");

  const load = () => {
    endpoints.adminGetSettings().then((r) => setS(r.data)).catch(() => {});
    endpoints.adminCategories().then((r) => setCats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const [guideBusy, setGuideBusy] = useState(false);
  const downloadGuide = async () => {
    setGuideBusy(true);
    try {
      const r = await endpoints.adminSystemGuide();
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "BB_Kigali_System_Guide.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("System guide downloaded");
    } catch (e) { handleErr(e); } finally { setGuideBusy(false); }
  };

  const saveSettings = async () => {
    try { await endpoints.adminUpdateSettings(s); toast.success("Settings saved"); }
    catch (e) { handleErr(e); }
  };
  const addCat = async () => {
    if (!newCat) return;
    try { await endpoints.adminCreateCategory({ name: newCat, isActive: true }); setNewCat(""); toast.success("Category added"); load(); }
    catch (e) { handleErr(e); }
  };
  const delCat = async (id) => { try { await endpoints.adminDeleteCategory(id); load(); } catch (e) { handleErr(e); } };
  const saveCat = async (id, patch) => { try { await endpoints.adminUpdateCategory(id, patch); toast.success("Category updated"); load(); } catch (e) { handleErr(e); } };

  if (!s) return <Loading />;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  return (
    <div>
      <PageHeader title="Settings" desc="Station branding, streaming and integrations." />

      <Card className="p-6 mb-6">
        <h3 className="text-white font-bold flex items-center gap-2 mb-2"><FileDown className="w-5 h-5 text-[#E11D2A]" /> System Guide</h3>
        <p className="text-slate-400 text-sm mb-4">Download the complete guide explaining every menu and feature of the B&amp;B KIGALI FM system — for you and your team.</p>
        <Btn onClick={downloadGuide} data-testid="download-system-guide" disabled={guideBusy}>{guideBusy ? "Preparing…" : "Download System Guide (PDF)"}</Btn>
      </Card>


      <Card className="p-6 mb-6">
        <h3 className="text-white font-bold flex items-center gap-2 mb-4"><Settings className="w-5 h-5 text-[#E11D2A]" /> Station</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Station name" value={s.stationName || ""} onChange={set("stationName")} data-testid="settings-name-input" />
          <Field label="Frequency" value={s.frequency || ""} onChange={set("frequency")} />
          <Field label="Tagline" value={s.stationTagline || ""} onChange={set("stationTagline")} className="sm:col-span-2" />
          <Field label="Contact email (Contact page)" value={s.contactEmail || ""} onChange={set("contactEmail")} placeholder="support@bbkigali.com" data-testid="settings-contact-email" />
          <Field label="Contact phone (Contact page)" value={s.contactPhone || ""} onChange={set("contactPhone")} placeholder="+250 798 875 272" data-testid="settings-contact-phone" />
          <Field label="WhatsApp support number" value={s.contactWhatsapp || ""} onChange={set("contactWhatsapp")} placeholder="250791446979" data-testid="settings-contact-whatsapp" />
          <Field label="Address" value={s.contactAddress || ""} onChange={set("contactAddress")} placeholder="Kigali, Rwanda" data-testid="settings-contact-address" />
          <div className="sm:col-span-2"><ImageUpload label="Station logo" value={s.logoUrl || ""} onChange={(url) => setS({ ...s, logoUrl: url })} testid="settings-logo" aspect="aspect-[3/1]" /></div>
        </div>
        <h3 className="text-white font-bold flex items-center gap-2 mt-6 mb-4"><Radio className="w-5 h-5 text-[#E11D2A]" /> Streaming</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Live radio stream URL (Monetix)" value={s.radioStreamUrl || ""} onChange={set("radioStreamUrl")} data-testid="settings-stream-input" />
          <Field label="Live radio stream URL (HTTPS, used by player)" value={s.radioStreamUrlHttps || ""} onChange={set("radioStreamUrlHttps")} />
          <Field label="Icecast status URL (now-playing)" value={s.icecastStatusUrl || ""} onChange={set("icecastStatusUrl")} placeholder="…/status-json.xsl" />
          <Field label="YouTube live URL" value={s.youtubeLiveUrl || ""} onChange={set("youtubeLiveUrl")} />
        </div>
        <Btn onClick={saveSettings} data-testid="settings-save-btn" className="mt-5">Save settings</Btn>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1"><DollarSign className="w-5 h-5 text-[#E11D2A]" /> Plan pricing & currencies</h3>
        <p className="text-xs text-slate-400 mb-4">Manage subscription prices for every currency (RWF, EUR, USD and more), add new currencies and set which countries use them — all from the dedicated pricing page.</p>
        <a href="/admin/pricing" data-testid="go-to-pricing-page"><Btn>Open Pricing & Currencies</Btn></a>
      </Card>

      <Card className="p-6">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1"><Tags className="w-5 h-5 text-[#E11D2A]" /> Categories & VOD display</h3>
        <p className="text-xs text-slate-400 mb-4">Control which categories customers see in the Video/VOD menu and on the homepage, their display name and order. Hiding a category never deletes its content.</p>
        <div className="hidden sm:grid grid-cols-[1fr_5rem_6rem_7rem_2rem] gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-1 mb-1">
          <span>Display name</span><span>Order</span><span>In VOD</span><span>Homepage</span><span></span>
        </div>
        <div className="space-y-2 mb-4">
          {cats.map((c) => (
            <div key={c.id} data-testid={`cat-${c.id}`} className="grid grid-cols-2 sm:grid-cols-[1fr_5rem_6rem_7rem_2rem] gap-2 items-center rounded-lg bg-[#0A0C10] border border-[#273042] p-2">
              <input defaultValue={c.displayName || c.name} onBlur={(e) => e.target.value !== (c.displayName || c.name) && saveCat(c.id, { displayName: e.target.value })}
                data-testid={`cat-name-${c.id}`} className="h-9 px-2 rounded bg-[#121620] border border-[#273042] text-white text-sm col-span-2 sm:col-span-1" />
              <input type="number" defaultValue={c.order ?? 0} onBlur={(e) => saveCat(c.id, { order: Number(e.target.value) })}
                data-testid={`cat-order-${c.id}`} className="h-9 px-2 rounded bg-[#121620] border border-[#273042] text-white text-sm" />
              <button onClick={() => saveCat(c.id, { showInVod: c.showInVod === false })} data-testid={`cat-vod-${c.id}`}
                className={`h-9 rounded text-xs font-bold ${c.showInVod !== false ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{c.showInVod !== false ? "ON" : "OFF"}</button>
              <button onClick={() => saveCat(c.id, { showOnHomepage: !c.showOnHomepage })} data-testid={`cat-home-${c.id}`}
                className={`h-9 rounded text-xs font-bold ${c.showOnHomepage ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{c.showOnHomepage ? "ON" : "OFF"}</button>
              <button onClick={() => delCat(c.id)} data-testid={`cat-delete-${c.id}`} className="text-red-400 hover:text-red-300 text-lg">×</button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 max-w-sm">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="cat-name-input" placeholder="New category"
            className="h-10 flex-1 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
          <Btn onClick={addCat} data-testid="cat-add-btn"><Plus className="w-4 h-4" /> Add</Btn>
        </div>
      </Card>
    </div>
  );
}
